import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Spinner } from "reactstrap";
import { useCompanyCore } from "../../../../cores/company";
import { CompanyPayload } from "../../../../cores/company/interfaces/api/get-companies";
import { useRouterCore } from "../../../../cores/router";
import { PlanBadge } from "../../companies/PlanBadge";
import { RoleBadges } from "../../companies/RoleBadge";
import { PanelWembii } from "../../PanelWembii";
import { useIntl } from "react-intl";

interface Props {
  id: string;
}

export default function UserCompanies(props: Props) {
  const intl = useIntl();
  const { id } = props;

  const [companies, setCompanies] = useState<CompanyPayload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { getUserCompanies } = useCompanyCore();

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await getUserCompanies(id);
        if (!cancelled) {
          setCompanies(response || []);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [getUserCompanies, id]);

  return (
    <Container className="h-100" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div className="d-flex flex-column mt-4 py-4 px-2">
            <h2 className="h2 text-center mb-4">
              <FontAwesomeIcon
                icon={["fas", "building"]}
                className="mx-2"
                size="1x"
              />
              {intl.formatMessage({ id: "app.adminUsers.vinculatedCompanies" })}
            </h2>
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner color="primary" />
              </div>
            ) : companies.length === 0 ? (
              <p className="text-muted text-center mb-0">
                {intl.formatMessage({ id: "app.adminUsers.noLinkedCompanies" })}
              </p>
            ) : (
              <div className="mb-2 d-flex justify-content-center flex-column align-items-center ">
                {companies.map((company) => (
                  <CompanyRow key={company.company.id} id={id} company={company} />
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export const CompanyRow = ({
  company,
  id,
}: {
  company: CompanyPayload;
  id: string;
}) => {
  const { goToEditCompany } = useRouterCore();

  return (
    <PanelWembii
      className="w-100 px-4 py-3 my-2 pointer"
      shadow
      onClick={() => goToEditCompany(company.company.id)}
    >
      <Row className="d-flex align-items-center justify-content-center">
        <Col md="5">
          <h6>
            # {company.company.id} {company.company.name}
          </h6>
          {company.plan && <PlanBadge show plan={company.plan} />}
        </Col>
        <Col md="7">
          <div className="d-flex justify-content-end">
            {company.role && <RoleBadges roles={company.role} id={id} />}
          </div>
        </Col>
      </Row>
    </PanelWembii>
  );
};
